<template>
    <div :class="{
        'border-danger': hasUnitPromotion || hasNegativeMargin
    }, { 'overlay-spinner': loading }" class="bg-white rounded-top pt-0 mb-2">
        <div v-if="message" :class="['alert', messageType === 'success' ? 'alert-success' : 'alert-danger']"
             class="mt-2 text-center">
            {{ message }}
        </div>
        <div v-if="hasInsufficientStockForDiscounts" class="alert alert-warning text-center rounded-bottom-0 rounded-top" style="background-color: #fff3cd; border-color: #ffeaa7; color: #856404; border-radius: unset">
            Current stock levels are insufficient to display one or more quantity discounts. The discounts will be visible again when adequate stock is available.
        </div>
        <div class="overflow-auto w-99 m-1">
            <div class="row align-items-center d-inline-flex">
                <!-- Product Info -->
                <div class="col-4 col-md-6 col-sm-12">
                    <div class="mb-0 p-0 mt-0">
                        <p class="text-primary mb-0 mt-2">{{ product.id + ' - ' + product.name }}</p><br/>
                    </div>
                    <!-- Warning if unit-level promotion is active -->
                    <div v-if="hasUnitPromotion" class="">
                        <span class="badge bg-warning text-dark small text-truncate"
                            title="Quantity discounts will apply after the discount ends.">
                                ⚠️ This product has an active unit-level discount. Quantity discounts will be shown when unit-level discounts expire
                        </span>
                    </div>
                    <div class="row mt-0 ps-2">
                        <table class="table table-borderless fs-7 table-sm mb-0 text-nowrap min-width-table">
                            <thead>
                            <tr>
                                <td class="text-muted">{{ product.sku }}</td>
                                <td class="text-muted">{{ product.cost }}</td>
                                <td class="text-muted">{{ this.product.prices?.[0]?.price ?? '-' }} {{ this.symbol }}</td>
                                <td class="text-muted">{{ unitSalePrice }} {{ this.symbol }}</td>
                                <td class="text-muted">{{ unitDiscount }}%</td>
                                <td class="text-muted">{{ unitMargin }}%</td>
                                <td class="text-muted">
                                    <span 
                                        class="badge text-uppercase py-1 px-2"
                                        :style="{
                                            borderRadius: '14px',
                                            backgroundColor: availableStock > 0 ? '#28a745' : '#dc3545',
                                            color: '#ffffff',
                                            fontSize: '0.65rem',
                                            fontWeight: '600'
                                        }"
                                    >
                                        {{ availableStock > 0 ? 'AVAILABLE' : 'OUT OF STOCK' }}
                                        <span class="badge ms-1 bg-secondary" style="border-radius: .25rem;">{{ availableStock }}</span>
                                    </span>
                                </td>
                            </tr>

                            </thead>
                            <tbody>
                            <tr class="text-muted text-nowrap fw-normal small">
                                <th class="fw-normal text-muted">SKU</th>
                                <th class="fw-normal text-muted">Cost</th>
                                <th class="fw-normal text-muted">Unit Original Price</th>
                                <th class="fw-normal text-muted">Unit Sale Price</th>
                                <th class="fw-normal text-muted">Unit Discount</th>
                                <th class="fw-normal text-muted">Unit Margin</th>
                                <th class="fw-normal text-muted">Available Stock</th>
                            </tr>
                            </tbody>
                        </table>

                    </div>
                </div>

                <!-- Quantity Tiers - Scrollable Horizontally -->
                <div class="col mt-3">
                    <div class="d-flex gap-1">
                        <div v-for="(tier, quantity) in visibleTiers" :key="quantity" class="flex-shrink-0">
                            <div :class="{
                                'bg-light': !tier.enabled,
                                'border-danger': tier.enabled && parseFloat(tier.marginPercentage) < 0,
                                'border-warning': tier.enabled && parseInt(quantity) > availableStock
                            }" class="card h-100 border rounded p-1 pe-4">
                                <div class="d-flex justify-content-between align-items-center mb-1">
                                    <strong>x{{ quantity }}</strong>
                                    <span v-if="tier.enabled && parseInt(quantity) > availableStock" class="text-warning small ms-2" title="Insufficient stock">⚠️</span>
                                    <div class="form-check form-switch">
                                        <input v-model="tier.enabled" class="form-check-input" type="checkbox"
                                               @change="onTierToggle(tier)"/>
                                    </div>
                                </div>
                                <div v-if="tier.error" class="text-danger small">{{ tier.error }}</div>
                                <div class="d-inline-flex gap-2 align-items-baseline">
                                    <div class="col-4">
                                        <input v-model.number="tier.unitSalePrice" class="form-control form-control-sm"
                                               min="0"
                                               step="0.01"
                                               type="number" @input="updateDiscountFromPrice(tier)"/>
                                        <label class="form-label small mb-0">Sale Price ({{ this.symbol }})</label>
                                    </div>

                                    <div class="col-4">
                                        <input v-model.number="tier.discountPercent"
                                               class="form-control form-control-sm"
                                               max="100"
                                               min="0"
                                               step="0.01" type="number" @input="updatePriceFromDiscount(tier)"/>
                                        <label class="form-label small mb-0">Discount (%)</label>
                                    </div>

                                    <div class="col-4">
                                        <input v-model.number="tier.marginPercentage" :disabled="true"
                                               class="form-control form-control-sm" max="100" min="0" step="0.01"
                                               type="number"/>
                                        <label class="form-label small mb-0">Margin (%)</label>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="col align-content-center align-items-center flex-shrink-0">
                            <button :disabled="hasTierError" class="btn btn-primary"
                                    @click="saveProduct">
                                <svg class="acorn-icons acorn-icons-save " fill="none" height="20"
                                     stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"
                                     stroke-width="1.5"
                                     viewBox="0 0 20 20" width="20"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M5.5 18H14.5C15.9045 18 16.6067 18 17.1111 17.6629C17.3295 17.517 17.517 17.3295 17.6629 17.1111C18 16.6067 18 15.9045 18 14.5V7.62041C18 6.93871 18 6.59786 17.8952 6.28697C17.849 6.14988 17.788 6.01821 17.7134 5.89427C17.5441 5.6132 17.2842 5.39268 16.7644 4.95163L14.2654 2.83122C13.8506 2.47926 13.6431 2.30328 13.403 2.19029C13.2968 2.14034 13.1865 2.09982 13.0732 2.06922C12.817 2 12.545 2 12.0009 2H5.5C4.09554 2 3.39331 2 2.88886 2.33706C2.67048 2.48298 2.48298 2.67048 2.33706 2.88886C2 3.39331 2 4.09554 2 5.5V14.5C2 15.9045 2 16.6067 2.33706 17.1111C2.48298 17.3295 2.67048 17.517 2.88886 17.6629C3.39331 18 4.09554 18 5.5 18Z">
                                    </path>
                                    <path
                                        d="M14 18V13.75C14 13.0478 14 12.6967 13.8315 12.4444C13.7585 12.3352 13.6648 12.2415 13.5556 12.1685C13.3033 12 12.9522 12 12.25 12H7.75C7.04777 12 6.69665 12 6.44443 12.1685C6.33524 12.2415 6.24149 12.3352 6.16853 12.4444C6 12.6967 6 13.0478 6 13.75V18">
                                    </path>
                                    <path
                                        d="M14 8L7.75 8C7.04777 8 6.69665 8 6.44443 7.83147C6.33524 7.75851 6.24149 7.66476 6.16853 7.55557C6 7.30335 6 6.95223 6 6.25L6 2">
                                    </path>
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    </div>
</template>
<script>
import axios from "axios";

export default {
    props: {
        product: {
            type: Object,
            required: true
        },
        salesChannel: {
            type: Number,
            required: true
        }
    },
    data() {
        const initialTiers = {
            3: {
                enabled: false,
                unitSalePrice: null,
                discountPercent: null,
                marginPercentage: null,
                error: null
            },
            6: {
                enabled: false,
                unitSalePrice: null,
                discountPercent: null,
                marginPercentage: null,
                error: null
            },
            12: {
                enabled: false,
                unitSalePrice: null,
                discountPercent: null,
                marginPercentage: null,
                error: null
            },
            18: {
                enabled: false,
                unitSalePrice: null,
                discountPercent: null,
                marginPercentage: null,
                error: null
            },
            24: {
                enabled: false,
                unitSalePrice: null,
                discountPercent: null,
                marginPercentage: null,
                error: null
            },
            36: {
                enabled: false,
                unitSalePrice: null,
                discountPercent: null,
                marginPercentage: null,
                error: null
            },
            60: {
                enabled: false,
                unitSalePrice: null,
                discountPercent: null,
                marginPercentage: null,
                error: null
            }
        };

        return {
            tiers: initialTiers,
            symbol: '€',
            activeTierCount: 0,
            loading: false,
            message: '',
            messageType: '',
            serverMargin : null
        };
    },
    computed: {
        visibleTiers() {
            // Channel 1 is B2B_IT
            if (this.salesChannel === 1) {
                return this.tiers;
            }
            
            // For other channels, filter out 36 and 60
            const { 36: _, 60: __, ...standardTiers } = this.tiers;
            return standardTiers;
        },
        activeDiscount() {
            const discounts = this.product.prices?.[0]?.discounts;
            if (!discounts?.length) return null;

            const now = new Date();
            // Find the first discount that is enabled and within date range
            return discounts.find(discount => {
                const startDate = new Date(discount.startDate);
                const endDate = new Date(discount.endDate);
                const isEnabled = discount.enabled === 1 || discount.enabled === true;
                const isValid = startDate <= now && now <= endDate;

                return isEnabled && isValid;
            });
        },
        hasUnitPromotion() {
            return !!this.activeDiscount;
        },
        hasNegativeMargin() {
            return Object.values(this.tiers).some(t => t.enabled && parseFloat(t.marginPercentage) < 0);
        },
        hasTierError() {
            return Object.values(this.tiers).some(t => t.error !== null);
        },
        unitDiscount() {
            const priceData = this.product.prices?.[0];

            if (!priceData || !priceData.price) return 0;

            const productCost = priceData.price;
            const productPrice = this.unitSalePrice;

            if (this.activeDiscount) {
                const discountSalePrice = this.activeDiscount.salePrice;
                if (discountSalePrice > 0 && productPrice > 0) {
                    return ((productCost - discountSalePrice) / productPrice * 100).toFixed(2);
                }
            }

            return 0;
        },
        unitSalePrice() {
            if (this.activeDiscount) {
                return this.activeDiscount.salePrice;
            } else {
                return this.product.prices?.[0]?.price ?? 0;
            }

        },
        unitMargin() {
            // 1. synchronous fallback
            const cost = parseFloat(this.product.cost) || 0;
            const price = parseFloat(this.unitSalePrice) || 0;
            if (price <= 0 || cost <= 0) return '0.00';
            const fallback = (((price - cost) / price) * 100).toFixed(2);

            // 2. use cached server value if we already have one
            if (this.serverMargin !== null) return this.serverMargin;

            // 3. otherwise hit the endpoint once per product
            this.fetchMarginFromServer(price, cost);

            return fallback;
        },
        availableStock() {
            return this.product.stock?.ecommerceAvailableAmount ?? 0;
        },
        hasInsufficientStockForDiscounts() {
            return Object.entries(this.tiers).some(([qty, tier]) => {
                return tier.enabled && parseInt(qty) > this.availableStock;
            });
        }
    },
    methods: {
        async fetchMarginFromServer(price, cost) {
            if (!this.product.vatClassId) return 0.00;

            try {
                const { data } = await axios.post('/comp/product-price/calculate-margin', {
                    price,
                    cost,
                    vatClassId: this.product.vatClassId,
                    countryCode: 'IT'
                });
                this.serverMargin = parseFloat(data.marginPerc).toFixed(2);
            } catch (e) {
                console.warn('Server margin call failed', e);
                return 0.00;

            }
        },
        onTierToggle(tier) {
            const activeTiers = Object.values(this.tiers).filter(t => t.enabled);
            if (activeTiers.length > 2) {
                tier.enabled = 0;
                alert("You can only activate 2 quantity discounts at the same time.");
            }
        },

        updateDiscountFromPrice(tier) {
            if (tier.unitSalePrice !== null) {
                if (tier.unitSalePrice < 0) {
                    tier.error = 'You cannot set a negative price.';
                    tier.discountPercent = null;
                } else if (tier.unitSalePrice > this.unitSalePrice) {
                    tier.error = 'Discount price cannot be greater than the original price.';
                    tier.discountPercent = null;
                } else {
                    tier.error = null;
                    if (this.unitSalePrice > 0) {
                        tier.discountPercent = (((this.unitSalePrice - tier.unitSalePrice) / this.unitSalePrice) * 100).toFixed(2);
                    } else {
                        tier.discountPercent = null;
                    }
                }
            }
            this.updateMargin(tier);
        },

        updatePriceFromDiscount(tier) {
            if (tier.discountPercent !== null) {
                if (tier.discountPercent < 0) {
                    tier.error = 'You cannot set a negative discount percentage.';
                    tier.unitSalePrice = null;
                    return;
                }
                tier.error = null;
                if (this.product.prices?.[0] && this.product.prices[0].price > 0) {
                    let tierPrice = this.product.prices[0].price * (1 - tier.discountPercent / 100);
                    
                    // ROUND UP to nearest 0.10
                    // Example: 94.16 -> 94.20
                    tierPrice = Math.ceil(tierPrice * 10) / 10;
                    
                    if (tierPrice < 0) {
                        tier.error = 'You cannot set a negative price.';
                        tier.unitSalePrice = null;
                        return;
                    } else if (tierPrice > this.unitSalePrice) {
                        tier.error = 'Discount price cannot be greater than the original price.';
                        tier.unitSalePrice = null;
                        return;
                    }
                    tier.unitSalePrice = tierPrice.toFixed(2);
                } else {
                    tier.unitSalePrice = null;
                }
            }
            this.updateMargin(tier);
        },

        updateMargin(tier) {
            if (tier.unitSalePrice !== null && this.product.cost > 0) {
                tier.marginPercentage = (((tier.unitSalePrice - this.product.cost) / tier.unitSalePrice) * 100).toFixed(2);
            } else {
                tier.marginPercentage = 0.00;
            }
        },

        calculateMarginPercentage(product, volumeDiscount) {
            if (volumeDiscount.unitSalePrice !== null && product.cost > 0) {
                return (((volumeDiscount.unitSalePrice - product.cost) / volumeDiscount.unitSalePrice) * 100).toFixed(2);
            } else {
                return 0.00;
            }
        },

        saveProduct() {
            if (this.hasNegativeMargin) {
                const confirmContinue = confirm("The quantity discount generates a negative margin, do you wish to continue?");
                if (!confirmContinue) return;
            }
            this.loading = true
            const url = `quantity-discount/${this.product.id}/${this.salesChannel}/save-product`;
            
            const payload = {
                tiers: this.tiers,
                productPriceId: this.product.prices?.[0]?.id ?? null
            };

            axios.post(url, payload)
                .then(response => {
                    this.loading = false
                    this.messageType = response.data.success === true ? 'success' : 'error'
                    this.message = response.data.message
                })
                .catch(error => {

                    this.loading = false;
                    this.messageType = 'error';

                    if (error.response && error.response.data) {
                        const responseData = error.response.data;

                        if (responseData.message) {
                            this.message = ` ${responseData.message}`;
                        } else if (typeof responseData === 'object') {
                            const allMessages = Object.values(responseData)
                                .flat()
                                .join(', ');
                            this.message = ` ${allMessages}`;
                        } else {
                            this.message = 'An error occurred while saving the product.';
                        }
                    } else {
                        this.message = 'Network or server error. Please try again.';
                    }
                })
                .finally(() => {
                    setTimeout(() => {
                        this.message = '';
                        this.messageType = '';
                    }, 5000);
                });
        }
    },
    mounted() {
        // Populate existing tiers from product
        this.symbol = this.salesChannel === 6 ? '£' : '€';
        const priceData = this.product.prices?.[0];
        if (priceData) {
            const volumeDiscounts = priceData.volumeDiscounts || priceData.volume_discounts;
            if (volumeDiscounts) {
                volumeDiscounts.forEach(vd => {
                    const quantity = (vd.quantityDiscount || vd.quantity_discount || "").toString();
                    if (this.tiers[quantity]) {
                        this.tiers[quantity] = {
                            ...this.tiers[quantity],
                            enabled: vd.enabled,
                            unitSalePrice: vd.unitSalePrice || vd.unit_sale_price,
                            discountPercent: vd.discountPercent || vd.discount_percent,
                            marginPercentage: this.calculateMarginPercentage(this.product, {
                                unitSalePrice: vd.unitSalePrice || vd.unit_sale_price
                            })
                        };
                    }
                });
            }
        }
    }
};
</script>

<style scoped>
.customWidth {
    width: 100%;
}

table.table-sm th,
table.table-sm td {
    font-size: 0.80rem;

    vertical-align: middle;
}

/* ---------- Professional scrollbar for the tier slider ---------- */
.overflow-auto.w-100 {
    /* make the container the reference for the pseudo-elements */
    position: relative;
    /* macOS/iOS momentum scroll */
    -webkit-overflow-scrolling: touch;
    /* always reserve space so content doesn’t jump when the bar appears */
    scrollbar-gutter: stable;
}

/* ----- Web-kit browsers (Chrome, Edge, Safari) ----- */
.overflow-auto.w-100::-webkit-scrollbar {
    height: 6px; /* thickness of the horizontal track */
}

.overflow-auto.w-100::-webkit-scrollbar-track {
    background: transparent; /* invisible by default */
}

.overflow-auto.w-100::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, .20); /* subtle dark grey */
    border-radius: 3px;
    transition: background .2s ease;
}

.overflow-auto.w-100:hover::-webkit-scrollbar-thumb,
.overflow-auto.w-100:focus-within::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, .40); /* darker on hover/focus */
}

/* ----- Firefox ----- */
.overflow-auto.w-100 {
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, .35) transparent;
}

</style>
